import React, { useMemo } from 'react'
import $t from '../../../../i18n'
import { useDispatch, useSelector } from 'react-redux'
import Actions from '../../../../actions'
import Page from '../../../common/Page/Page'
import { clinicTypes } from '../../../../consts'
import { getIsHmoMember } from '../../../../utils/hmoUtils'
import config from '../../../../config.json'

import './QueueAppointmentsPage.css'
import {
  filterAppointmentsPerMember,
  filterMHAppointments,
  filterPrimaryQueueAppointments,
} from '../../../../utils/appointmentsUtils'
import { Button } from 'antd'
import AppointmentStatusCard from '../../../common/AppointmentStatusCard'
import { isInClinic } from '../../../../utils/clinicsUtils'
import { isMemberMinorChild } from '../../../../utils/familyUtils'

export const QueueAppointmentsPage = ({ onNext }) => {
  const dispatch = useDispatch()

  const selectedMember = useSelector(state => state.account.selectedMember)
  const userDetails = useSelector(state => state.profile.userDetails)
  const isRescheduleLoading = useSelector(
    state => state.getCare?.isRescheduleLoading
  )

  const appointments = useSelector(state => state.appointments?.appointments)

  const isInMHClinic = useSelector(state =>
    isInClinic(state, clinicTypes.MENTAL)
  )

  const memberId = selectedMember ? selectedMember?.id : userDetails?.id
  const isHMOMember = getIsHmoMember(selectedMember || userDetails)
  const isMinor = selectedMember ? isMemberMinorChild(selectedMember) : false

  const appointmentsPerMember = useMemo(
    () => filterAppointmentsPerMember(appointments, memberId),
    [appointments, memberId]
  )

  const primaryAppointments = useMemo(
    () => filterPrimaryQueueAppointments(appointmentsPerMember),
    [appointmentsPerMember]
  )

  // for hmo users, show all appointments (primary/mental)
  // fot tele users, show only primary appointments
  const displayAppointments = isHMOMember
    ? appointmentsPerMember
    : primaryAppointments

  const isMHAppointmentExist = useMemo(
    () => !!filterMHAppointments(appointmentsPerMember)?.length,
    [appointmentsPerMember]
  )

  const isPrimaryCreatedByPatientExist = useMemo(
    () => !!primaryAppointments?.find(app => !!app?.is_created_by_patient),
    [primaryAppointments]
  )

  // the schedule more option should be visible just in can we don't have any primary visit created by member
  // or if we don't have any mental health appointment and we are not in mental health clinic (mh followup)
  const isScheduleAnotherVisitOptionExist =
    !isHMOMember || !isPrimaryCreatedByPatientExist

  // for HMO members only we have the option to schedule on mental visit and one primary visit (that was schedule by member)
  const isUserHitMaxAppointments =
    isHMOMember && isPrimaryCreatedByPatientExist && isMHAppointmentExist

  const onMentalClick = () => {
    if (isInMHClinic) {
      window.open(config.whatsappURLInsurence, '_blank')
    } else {
      // continue to schedule mental health appointment
      // this action changes the selected service to 'mental'
      // no need for next function (the get care will render again when selected service changes)
      dispatch(
        Actions.createAction(Actions.HMO_BOT_STARTED, { isMentalHealth: true })
      )
    }
  }

  return (
    <Page
      className="appointments-queue-page"
      title={$t('queueAppointmentsPage.title')}
      loading={isRescheduleLoading}
      subtitle={
        isHMOMember && !isScheduleAnotherVisitOptionExist
          ? isUserHitMaxAppointments
            ? $t('queueAppointmentsPage.subtitleHMOMax')
            : $t('queueAppointmentsPage.subtitleHMO')
          : $t('queueAppointmentsPage.subtitle')
      }
    >
      {isUserHitMaxAppointments && (
        <div className="appointments-max-message">
          {$t('queueAppointmentsPage.maxLimitNote')}
        </div>
      )}
      <div className="queue-appointments-content">
        <div className="appointment-list">
          {displayAppointments?.map(appointment => (
            <AppointmentStatusCard
              key={appointment?.id}
              appointment={appointment}
            />
          ))}
        </div>
        {isScheduleAnotherVisitOptionExist && (
          <Button
            type="primary"
            className="schedule-another-button"
            onClick={onNext}
          >
            {$t('queueAppointmentsPage.scheduleAnother')}
          </Button>
        )}
        {!isMHAppointmentExist &&
          isHMOMember &&
          !isScheduleAnotherVisitOptionExist &&
          !isMinor && (
            <div className="mh-appointment-message">
              {isInMHClinic
                ? $t('queueAppointmentsPage.mentalScheduleSupport')
                : $t('queueAppointmentsPage.mentalSchedule')}
              <Button type="link" onClick={onMentalClick}>
                {isInMHClinic
                  ? $t('queueAppointmentsPage.mentalScheduleSupportLink')
                  : $t('queueAppointmentsPage.mentalScheduleLink')}
              </Button>
            </div>
          )}
      </div>
    </Page>
  )
}

export default QueueAppointmentsPage
